import { Currencies, Currency, Money } from '@afterpay/types'

export interface GiftCardPromoBreakdown {
  faceValue: Money
  discountAmount: Money
  displayName: string
  purchaseAmount: Money
}

interface ParsedMoney {
  money: Money
  minorUnits: number
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const isCurrency = (value: unknown): value is Currency => Object.values(Currencies).some((currency) => currency === value)

const parseMoney = (value: unknown): ParsedMoney | null => {
  if (!isRecord(value) || typeof value.amount !== 'string' || !isCurrency(value.currency)) {
    return null
  }

  const match = /^(\d{1,14})(?:\.(\d{1,2}))?$/.exec(value.amount)
  if (!match) {
    return null
  }

  const minorUnits = Number(`${match[1]}${(match[2] ?? '').padEnd(2, '0')}`)
  if (!Number.isSafeInteger(minorUnits) || minorUnits <= 0) {
    return null
  }

  return { money: { amount: value.amount, currency: value.currency }, minorUnits }
}

export const parseGiftCardPromoBreakdown = (checkoutAmount: unknown, custom: unknown, displayedCurrency?: unknown): GiftCardPromoBreakdown | null => {
  if (!isRecord(custom) || !isRecord(custom.giftCardPromotion)) {
    return null
  }

  const { displayName, discountAmount } = custom.giftCardPromotion
  if (typeof displayName !== 'string' || displayName.trim() === '') {
    return null
  }

  const purchase = parseMoney(checkoutAmount)
  const face = parseMoney(custom.giftCardAmount)
  const discount = parseMoney(discountAmount)
  if (!purchase || !face || !discount) {
    return null
  }

  const currency = purchase.money.currency
  if (
    face.money.currency !== currency ||
    discount.money.currency !== currency ||
    (displayedCurrency !== undefined && displayedCurrency !== currency) ||
    face.minorUnits - discount.minorUnits !== purchase.minorUnits
  ) {
    return null
  }

  return {
    faceValue: face.money,
    discountAmount: discount.money,
    displayName,
    purchaseAmount: purchase.money,
  }
}
